import { EventType } from "@/analytics/analytics-events";
import type { Set3DModelVisibilityDistanceProperties } from "@/analytics/viewer-settings-events-properties";
import { isMetric } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { useCadVisibilityDistance } from "@/hooks/use-cad-visibility-minimum-distance";
import {
  convertFromMeter,
  FaroTooltip,
  MeasurementUnits,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { outOfCoreStreamCadModelSettings } from "@faro-lotv/lotv";
import { useCallback, useMemo, type JSX } from "react";
import { ViewOptionsSlider } from "./view-options-slider";

/**
 * @returns the distance in meter corresponding to a given tick on the slider
 * @param tick position of the slider to convert to distance in meter
 */
function tickToDistance(tick: number): number {
  return Math.pow(2, tick);
}

/**
 * @returns the number of tick in slider space corresponding to a given distance in meters
 * @param distance distance in meter to convert to slider tick
 */
function distanceToTick(distance: number): number {
  return Math.log2(distance);
}

/** @returns a slider component to set the visibility distance. */
export function CadDistanceFilteringSlider(): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const isMetricUnit = useMemo(() => isMetric(distanceUnit), [distanceUnit]);
  const {
    cadVisibilityDistanceDisabled,
    cadVisibilityDistanceInLocalStorage,
    setCadVisibilityDistanceInLocalStorage,
  } = useCadVisibilityDistance();

  const valueLabelFormat = useCallback(
    (ticks: number) => {
      const distance = tickToDistance(ticks);
      if (isMetricUnit) {
        return `${distance.toFixed(0)} m`;
      }

      const feetDistance = convertFromMeter(distance, MeasurementUnits.feet);
      return `${feetDistance.toFixed(0)} ft`;
    },
    [isMetricUnit],
  );

  return (
    <FaroTooltip
      title={
        cadVisibilityDistanceDisabled
          ? "3D Model visibility only applies in Walk mode"
          : undefined
      }
    >
      <ViewOptionsSlider
        disabled={cadVisibilityDistanceDisabled}
        label="3D Model visibility"
        title="3DModel Visibility"
        description="Configure the minimum distance at which the active model will be visible in the current 3D scene. Only applies to Walk mode."
        onClick={() => {
          Analytics.track(EventType.openModelVisibilityHelp);
        }}
        step={0.1}
        min={2}
        max={10}
        value={distanceToTick(cadVisibilityDistanceInLocalStorage)}
        valueLabelFormat={valueLabelFormat}
        valueLabelDisplay="auto"
        onChange={(_, valueInTick) => {
          if (Array.isArray(valueInTick)) {
            return;
          }
          const newDistance = tickToDistance(valueInTick);
          setCadVisibilityDistanceInLocalStorage(newDistance);
          outOfCoreStreamCadModelSettings.minVisibleDistance = newDistance;
        }}
        onPointerUp={() => {
          Analytics.track<Set3DModelVisibilityDistanceProperties>(
            EventType.set3DModelVisibilityDistance,
            {
              value: Number(cadVisibilityDistanceInLocalStorage.toFixed(2)),
            },
          );
        }}
      />
    </FaroTooltip>
  );
}
